import React, { useContext, useEffect, useRef } from 'react';

import { useSnackbar } from 'notistack';

import {
    AppBar,
    Box,
    Toolbar,
    SxProps,
    Typography,
    Stack,
    useTheme,
    useMediaQuery,
} from '@mui/material';

import {
    CustomizerReducerType,
    BDD_SWITCH_SOCIETES_FULLNAME_FROM_ID,
    useSociete,
} from '@europrocurement/l2d-domain';

import { SidebarFoldedWidth, SidebarWidth } from '@europrocurement/flexy-components/assets';
import { faMessage, faBell } from '@fortawesome/sharp-regular-svg-icons';
import { DropdownPrescriber } from '../../atoms/DropdownPrescriber';
import { DropdownSociete } from '../../atoms/DropdownSociete';
import { useFullLayout } from '../../templates/FullLayout/FullLayoutContext';
import { ExitButton, ExitButtonProps } from '../../atoms/ExitButton';
import { ProfileDropdownOpener } from '../../molecules/ProfileDropdownOpener';
import { CloseSidebarButton } from '../../atoms/CloseSidebarButton/CloseSidebarButton';
import { EntryType } from '../../molecules';
import { IconBadgeList, IconBadgeListItem } from '../../molecules/IconBadgeList';
import { HeaderNotificationsContext } from './HeaderNotificationsContext';

export type HeaderProps = {
    sx?: SxProps;
    showSociete?: boolean;
    showPrescripteur?: boolean;
    handleClickExitButton?: ExitButtonProps['handleClickExitButton'];
    customHeader?: React.ReactNode;
    otherEntries?: EntryType[];
    manageAvatar?: React.ReactNode;
    itemsMessage?: IconBadgeListItem[];
    itemsNotification?: IconBadgeListItem[];
};

export const Header: React.FunctionComponent<HeaderProps> = function ({
    sx,
    showSociete = true,
    showPrescripteur = false,
    handleClickExitButton,
    customHeader = null,
    otherEntries,
    manageAvatar,
    itemsMessage,
    itemsNotification,
}: HeaderProps) {
    const { itemsMessage: ctxItemsMessage, itemsNotification: ctxItemsNotification } = useContext(
        HeaderNotificationsContext,
    );
    const resolvedItemsMessage = itemsMessage ?? ctxItemsMessage;
    const resolvedItemsNotification = itemsNotification ?? ctxItemsNotification;

    const xIdSociete = useSociete();

    const { enqueueSnackbar } = useSnackbar();

    const prevXIdSocieteRef = useRef<CustomizerReducerType['xIdSociete']>();

    useEffect(() => {
        const prevXIdSociete = prevXIdSocieteRef.current;

        if (prevXIdSociete !== undefined && prevXIdSociete !== xIdSociete) {
            enqueueSnackbar(
                <Stack
                    display="flex"
                    flexDirection="row"
                    alignItems="baseline"
                    gap={1}
                >
                    <Typography variant="caption">Nouvelle société sélectionnée :</Typography>
                    <Typography
                        variant="caption"
                        fontWeight="bold"
                    >
                        {BDD_SWITCH_SOCIETES_FULLNAME_FROM_ID[xIdSociete]}
                    </Typography>
                </Stack>,
                {
                    variant: 'info',
                    autoHideDuration: 5000,
                },
            );
        }

        prevXIdSocieteRef.current = xIdSociete;
    }, [xIdSociete, enqueueSnackbar]);

    const { isSidebarOpen, isProfileHeaderVisible } = useFullLayout();

    const theme = useTheme();

    const smBreakpoint = useMediaQuery(theme.breakpoints.up('sm'));

    const hiddenSidebarHeaderPaddingLeft = smBreakpoint ? SidebarFoldedWidth : 0;

    return (
        <AppBar
            sx={{
                backgroundColor: 'background.paper',
                backdropFilter: 'blur(8px)',
                ...sx,
                paddingLeft: isSidebarOpen
                    ? `${SidebarWidth}px !important`
                    : `${hiddenSidebarHeaderPaddingLeft}px !important`,
            }}
            elevation={0}
        >
            <Toolbar>
                {isSidebarOpen ? (
                    <CloseSidebarButton />
                ) : (
                    <ExitButton handleClickExitButton={handleClickExitButton} />
                )}
                {customHeader || null}

                {isProfileHeaderVisible ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            marginLeft: 'auto',
                            marginRight: 0,
                        }}
                    >
                        <IconBadgeList
                            tooltip="Messages"
                            items={resolvedItemsMessage ?? null}
                            icon={faMessage}
                            iconSize="sm"
                            count={
                                resolvedItemsMessage !== undefined && resolvedItemsMessage?.length
                                    ? resolvedItemsMessage?.length
                                    : 0
                            }
                        />
                        <IconBadgeList
                            items={resolvedItemsNotification ?? null}
                            icon={faBell}
                            iconSize="sm"
                            count={resolvedItemsNotification?.length ?? 0}
                        />
                        <Box
                            sx={{
                                width: '1px',
                                backgroundColor: 'text.gris005',
                                height: '32px',
                                ml: 1,
                                mr: 1,
                            }}
                        />
                        {showSociete ? <DropdownSociete /> : null}
                        {showPrescripteur ? <DropdownPrescriber /> : null}
                        {showPrescripteur || showSociete || customHeader ? (
                            <Box
                                sx={{
                                    width: '1px',
                                    backgroundColor: 'text.gris005',
                                    height: '32px',
                                    ml: 1,
                                    mr: 1,
                                }}
                            />
                        ) : null}
                        <ProfileDropdownOpener
                            otherEntries={otherEntries}
                            manageAvatar={manageAvatar}
                        />
                    </Box>
                ) : null}
            </Toolbar>
        </AppBar>
    );
};
